import React, { useMemo, useState } from 'react';
import { Delete16Regular, Edit16Regular } from '@fluentui/react-icons';
import DoubleConfirmationModal from '../DoubleConfirmationModal/DoubleConfirmationModal';
import SimpleAlertModal from '../DoubleConfirmationModal/SimpleAlertModal';
import {
    DEFAULT_PROJECT_TYPE_COLOR,
    isSystemProjectType,
    normalizeProjectTypeOptions,
    projectTypeNamesFromOptions,
    SYSTEM_PROJECT_TYPES,
} from '../../../utils/projectTypes';
import styles from '../../WorkspaceSettings/css/index.module.css';

const ProjectTypesEditor = ({ projectTypes, onChange, projects = [], inputRowClassName, inputClassName, addButtonClassName }) => {
    const normalizedTypes = useMemo(() => normalizeProjectTypeOptions(projectTypes), [projectTypes]);
    const activeNames = useMemo(() => new Set(projectTypeNamesFromOptions(normalizedTypes)), [normalizedTypes]);
    const customActiveTypes = useMemo(() => normalizedTypes.filter(type => !isSystemProjectType(type.name)), [normalizedTypes]);

    const [newProjectType, setNewProjectType] = useState('');
    const [editingTypeName, setEditingTypeName] = useState('');
    const [editingOriginalName, setEditingOriginalName] = useState('');
    const [cannotDeleteTypeName, setCannotDeleteTypeName] = useState('');

    const updateProjectTypes = nextList => {
        onChange(normalizeProjectTypeOptions(nextList));
    };

    const handleAddCatalogType = name => {
        if (activeNames.has(name)) return;
        updateProjectTypes([...normalizedTypes, { name, color: DEFAULT_PROJECT_TYPE_COLOR }]);
    };

    const handleAddCustomProjectType = () => {
        const label = (newProjectType || '').trim();
        if (!label) return;
        if (activeNames.has(label)) {
            setNewProjectType('');
            return;
        }
        updateProjectTypes([...normalizedTypes, { name: label, color: DEFAULT_PROJECT_TYPE_COLOR }]);
        setNewProjectType('');
    };

    const handleDeleteProjectType = name => {
        const inUse = projects.some(project => project?.project_type === name);
        if (inUse) {
            setCannotDeleteTypeName(name);
            return;
        }
        updateProjectTypes(normalizedTypes.filter(type => type.name !== name));
    };

    const startEditProjectType = currentName => {
        setEditingOriginalName(currentName);
        setEditingTypeName(currentName || '');
    };

    const cancelEditProjectType = () => {
        setEditingOriginalName('');
        setEditingTypeName('');
    };

    const handleSaveProjectTypeEdit = () => {
        const label = (editingTypeName || '').trim();
        if (!label || !editingOriginalName) {
            cancelEditProjectType();
            return;
        }
        const nextList = normalizedTypes.map(type =>
            type.name === editingOriginalName
                ? {
                      ...type,
                      name: label,
                  }
                : type,
        );
        updateProjectTypes(nextList);
        cancelEditProjectType();
    };

    const inputRowClasses = inputRowClassName || styles.projectTypeInputRow;
    const inputClasses = inputClassName || styles.projectTypeInput;
    const addButtonClasses = addButtonClassName || styles.addTypeButton;

    return (
        <>
            {cannotDeleteTypeName ? (
                <SimpleAlertModal
                    open={!!cannotDeleteTypeName}
                    onClose={() => setCannotDeleteTypeName('')}
                    message="The selected project type cannot be deleted because there are active projects associated with it."
                />
            ) : null}

            <ul className={styles.projectTypesList}>
                {SYSTEM_PROJECT_TYPES.map(name => {
                    const isActive = activeNames.has(name);
                    return (
                        <li key={name} className={styles.projectTypeRow}>
                            <span className={styles.projectTypeName}>{name}</span>
                            <div className={styles.projectTypeActions}>
                                {isActive ? (
                                    <DoubleConfirmationModal
                                        handleAction={() => handleDeleteProjectType(name)}
                                        message="Deleting the selected project type will permanently remove it from the workspace.">
                                        <Delete16Regular className="me-3" color="var(--Neutral-Gray-6)" type="button" />
                                    </DoubleConfirmationModal>
                                ) : (
                                    <button type="button" className={styles.addCatalogTypeButton} onClick={() => handleAddCatalogType(name)}>
                                        Add
                                    </button>
                                )}
                            </div>
                        </li>
                    );
                })}

                {customActiveTypes.map(type => (
                    <li key={type.name} className={styles.projectTypeRow}>
                        {editingOriginalName === type.name ? (
                            <>
                                <input
                                    type="text"
                                    className={styles.projectTypeEditInput}
                                    value={editingTypeName}
                                    onChange={e => setEditingTypeName(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleSaveProjectTypeEdit();
                                        } else if (e.key === 'Escape') {
                                            e.preventDefault();
                                            cancelEditProjectType();
                                        }
                                    }}
                                />
                                <div className={styles.projectTypeActions}>
                                    <button type="button" className={styles.saveTypeButton} onClick={handleSaveProjectTypeEdit}>
                                        Save
                                    </button>
                                    <button type="button" className={styles.deleteTypeButton} onClick={cancelEditProjectType}>
                                        Cancel
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <span className={styles.projectTypeName}>{type.name}</span>
                                <div className={styles.projectTypeActions}>
                                    <Edit16Regular
                                        className="me-3"
                                        color="var(--Neutral-Gray-6)"
                                        type="button"
                                        onClick={() => startEditProjectType(type.name)}
                                    />
                                    <DoubleConfirmationModal
                                        handleAction={() => handleDeleteProjectType(type.name)}
                                        message="Deleting the selected project type will permanently remove it from the workspace.">
                                        <Delete16Regular className="me-3" color="var(--Neutral-Gray-6)" type="button" />
                                    </DoubleConfirmationModal>
                                </div>
                            </>
                        )}
                    </li>
                ))}
            </ul>

            <div className={inputRowClasses}>
                <input
                    type="text"
                    className={inputClasses}
                    placeholder="Add Custom Project Type"
                    value={newProjectType}
                    onChange={e => setNewProjectType(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCustomProjectType();
                        }
                    }}
                />
                <button type="button" className={addButtonClasses} onClick={handleAddCustomProjectType} disabled={!newProjectType.trim()}>
                    Add
                </button>
            </div>
        </>
    );
};

export default ProjectTypesEditor;
